import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePublicWebSocketContext } from '../../contexts/PublicWebSocketContext';

/**
 * VAR Review Overlay
 *
 * Shows VAR review banner during checking and decision phases.
 * Self-managed: listens to WebSocket events directly.
 *
 * Phases:
 * - checking: Blue banner "VAR - CHECKING GOAL" (stays until decision)
 * - decision: Green (confirmed) or Red (overturned) banner (auto-dismiss 6s)
 * - cancelled: Immediately hides
 *
 * z-index: 45 (above clock z-40, below debug z-50)
 */
export const VARReviewOverlay = ({ wsData }) => {
  const { socket, connected } = usePublicWebSocketContext();
  const [varState, setVarState] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef(null);

  const handleReviewStart = useCallback((data) => {
    console.log('[VAROverlay] Review started:', data);
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVarState({
      phase: 'checking',
      eventId: data.eventId,
      reviewType: data.reviewType,
      description: data.description,
      event: data.event
    });
    setIsVisible(true);
  }, []);

  const handleReviewDecision = useCallback((data) => {
    console.log('[VAROverlay] Decision received:', data);

    if (data.decision === 'cancelled') {
      setIsVisible(false);
      setTimeout(() => setVarState(null), 500);
      return;
    }

    setVarState({
      phase: 'decision',
      eventId: data.eventId,
      reviewType: data.reviewType,
      description: data.description,
      decision: data.decision,
      event: data.event
    });

    // Auto-dismiss after 6 seconds
    timerRef.current = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => setVarState(null), 500);
    }, 6000);
  }, []);

  useEffect(() => {
    if (!socket || !connected) return;

    socket.on('var:review:start', handleReviewStart);
    socket.on('var:review:decision', handleReviewDecision);

    return () => {
      socket.off('var:review:start', handleReviewStart);
      socket.off('var:review:decision', handleReviewDecision);
    };
  }, [socket, connected, handleReviewStart, handleReviewDecision]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (!varState) return null;

  const isChecking = varState.phase === 'checking';
  const isConfirmed = varState.decision === 'confirmed';
  const isOverturned = varState.decision === 'overturned';

  // Background colors per phase
  const getBgClass = () => {
    if (isChecking) return 'from-blue-900 to-blue-800';
    if (isConfirmed) return 'from-green-700 to-green-600';
    if (isOverturned) return 'from-red-700 to-red-600';
    return 'from-gray-700 to-gray-600';
  };

  // Decision icon
  const getDecisionIcon = () => {
    if (isChecking) return null;
    if (isConfirmed) return '\u2713';
    if (isOverturned) return '\u2717';
    return null;
  };

  // Format review type for display
  const formatReviewType = (type) => {
    if (!type) return '';
    return type.toUpperCase().replace(/_/g, ' ');
  };

  return (
    <div className="fixed bottom-24 left-0 right-0 flex justify-center px-6 pointer-events-none">
      <div
        className={`
          bg-gradient-to-r ${getBgClass()}
          text-white px-8 py-5 rounded-lg shadow-2xl
          transform transition-all duration-500 ease-out
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}
          max-w-5xl w-full
          border-2 ${isChecking ? 'border-blue-400' : isConfirmed ? 'border-green-400' : 'border-red-400'}
        `}
      >
        <div className="flex items-center justify-between">
          {/* Left: VAR label */}
          <div className="flex items-center gap-6">
            <div className="flex flex-col items-center">
              <div
                className={`text-5xl font-black tracking-widest ${isChecking ? 'animate-pulse' : ''}`}
                style={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}
              >
                VAR
              </div>
              {isChecking && (
                <div className="text-xs font-semibold opacity-75 tracking-wider mt-1">
                  REVIEW
                </div>
              )}
            </div>

            {/* Separator */}
            <div className={`w-px h-16 ${isChecking ? 'bg-blue-400' : isConfirmed ? 'bg-green-400' : 'bg-red-400'} opacity-50`} />

            {/* Description */}
            <div>
              <div className="text-3xl font-bold uppercase tracking-wide" style={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}>
                {varState.description}
              </div>
              {varState.event?.player && (
                <div className="text-lg font-semibold opacity-90 mt-1" style={{
                  textShadow: '1px 1px 2px rgba(0,0,0,0.6)'
                }}>
                  {varState.event.player.name} ({varState.event.team?.short_name || varState.event.team?.name})
                </div>
              )}
            </div>
          </div>

          {/* Right: Decision icon or minute */}
          <div className="text-right flex items-center gap-4">
            {getDecisionIcon() && (
              <div className="text-6xl font-black" style={{
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}>
                {getDecisionIcon()}
              </div>
            )}
            {varState.event?.minute && (
              <div>
                <div className="text-4xl font-black" style={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                }}>
                  {varState.event.minute}'
                </div>
                <div className="text-sm font-semibold opacity-80">
                  {varState.event.half === 1 ? '1ST HALF' : '2ND HALF'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VARReviewOverlay;
